import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Refresh, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './LoanRefinancingCalculatorPage/components/CalculationForm';
import ResultsOverview from './LoanRefinancingCalculatorPage/components/ResultsOverview';
import ComparisonChart from './LoanRefinancingCalculatorPage/components/ComparisonChart';
import QuickAdjustmentSidebar from './LoanRefinancingCalculatorPage/components/QuickAdjustmentSidebar';
import BankOffersSidebar from './LoanRefinancingCalculatorPage/components/BankOffersSidebar';
import TipsSidebar from './LoanRefinancingCalculatorPage/components/TipsSidebar';
import InfoContent from './LoanRefinancingCalculatorPage/components/InfoContent';
import PaymentSchedule from './LoanRefinancingCalculatorPage/components/PaymentSchedule';

// Определение начального состояния формы
export const initialFormData = {
  // Данные текущего кредита
  currentLoanAmount: '1000000',         // Остаток по текущему кредиту
  currentInterestRate: '14.5',          // Текущая процентная ставка
  currentRemainingTerm: '3',            // Оставшийся срок (в годах)
  currentMonthlyPayment: '',            // Текущий ежемесячный платеж (опционально)
  currentPaymentType: 'annuity',        // Тип платежа (аннуитетный/дифференцированный)
  alreadyPaid: '500000',                // Уже выплаченная сумма
  
  // Данные нового кредита (рефинансирование)
  newInterestRate: '9.5',               // Новая процентная ставка
  newLoanTerm: '3',                     // Новый срок кредита (в годах)
  additionalAmount: '0',                // Дополнительная сумма к рефинансированию
  newPaymentType: 'annuity',            // Тип платежа нового кредита
  startDate: new Date().toISOString().split('T')[0], // Дата начала нового кредита
  
  // Дополнительные параметры
  includeFees: true,                    // Учитывать комиссии
  initialFee: '10000',                  // Единоразовая комиссия
  earlyRepaymentFee: '0',               // Комиссия за досрочное погашение текущего кредита
  insuranceCost: '15000',               // Стоимость страховки
  valuationCost: '5000',                // Стоимость оценки (для ипотеки)
  
  // Предпочтения оптимизации
  optimizationGoal: 'payment',          // Цель: снижение платежа ('payment') или срока ('term')
  includeEarlyRepayment: false,         // Учитывать досрочное погашение
  earlyRepaymentAmount: '50000',        // Сумма досрочного погашения
  earlyRepaymentPeriod: 'monthly',      // Периодичность досрочного погашения
  earlyRepaymentStartMonth: '6',        // Месяц начала досрочных платежей
};

// Определение периодов досрочного погашения
export const earlyRepaymentPeriods = [
  { id: 'monthly', name: 'Ежемесячно' },
  { id: 'quarterly', name: 'Ежеквартально' },
  { id: 'semi_annually', name: 'Раз в полгода' },
  { id: 'annually', name: 'Ежегодно' },
  { id: 'once', name: 'Разово' }
];

// Определение банков для рефинансирования
export const banks = [
  { id: 'sberbank', name: 'Сбербанк', minRate: 8.9, maxRate: 15.9, maxTerm: 30, minAmount: 300000, maxAmount: 30000000 },
  { id: 'vtb', name: 'ВТБ', minRate: 9.2, maxRate: 15.6, maxTerm: 30, minAmount: 600000, maxAmount: 30000000 },
  { id: 'gazprombank', name: 'Газпромбанк', minRate: 8.7, maxRate: 16.2, maxTerm: 30, minAmount: 500000, maxAmount: 45000000 },
  { id: 'alfabank', name: 'Альфа-Банк', minRate: 8.5, maxRate: 17.1, maxTerm: 30, minAmount: 500000, maxAmount: 50000000 },
  { id: 'raiffeisen', name: 'Райффайзенбанк', minRate: 9.5, maxRate: 16.5, maxTerm: 25, minAmount: 700000, maxAmount: 26000000 },
  { id: 'tinkoff', name: 'Тинькофф Банк', minRate: 9.8, maxRate: 18.9, maxTerm: 20, minAmount: 300000, maxAmount: 15000000 },
  { id: 'psbank', name: 'Промсвязьбанк', minRate: 9.3, maxRate: 16.8, maxTerm: 25, minAmount: 500000, maxAmount: 20000000 },
  { id: 'rosbank', name: 'Росбанк', minRate: 9.4, maxRate: 16.9, maxTerm: 25, minAmount: 600000, maxAmount: 25000000 }
];

/**
 * LoanRefinancingCalculatorPage Component - Калькулятор рефинансирования кредита с анализом выгоды
 */
const LoanRefinancingCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('loan-calculator'), []);
  const pageTitle = 'Калькулятор рефинансирования кредита | Gensio';
  const metaDescription = 'Калькулятор рефинансирования кредита: расчет выгоды и экономии при рефинансировании, сравнение условий банков, анализ сроков и платежей, оценка целесообразности, построение графика платежей.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedComparisons, setSavedComparisons] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных сравнений из localStorage ---
  useEffect(() => {
    const savedComparisonsData = localStorage.getItem('savedRefinancingComparisons');
    if (savedComparisonsData) {
      try {
        setSavedComparisons(JSON.parse(savedComparisonsData));
      } catch (e) {
        console.error("Error loading saved comparisons:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'mortgage':
          newState.currentLoanAmount = '5000000';
          newState.currentInterestRate = '12';
          newState.currentRemainingTerm = '15';
          newState.valuationCost = '10000';
          newState.insuranceCost = '25000';
          break;
        case 'consumer':
          newState.currentLoanAmount = '500000';
          newState.currentInterestRate = '18';
          newState.currentRemainingTerm = '3';
          newState.valuationCost = '0';
          break;
        case 'early-repayment':
          newState = { ...initialFormData, includeEarlyRepayment: true };
          break;
        case 'additional-money':
          newState = { ...initialFormData, additionalAmount: '300000' };
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setComparisonData(null);
    setSchedule([]);
    setErrors({});
    setShowFullSchedule(false);

  }, [subToolId]);

  // Сохранение сравнения в localStorage
  const saveComparison = (name) => {
    if (!results || !comparisonData) return;

    const newSavedComparison = {
      id: Date.now().toString(),
      name: name || `Рефинансирование ${formData.currentLoanAmount}₽ (${new Date().toLocaleDateString('ru-RU')})`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      comparisonData: { ...comparisonData },
      bank: selectedBank
    };

    const updatedSavedComparisons = [...savedComparisons, newSavedComparison];
    setSavedComparisons(updatedSavedComparisons);
    localStorage.setItem('savedRefinancingComparisons', JSON.stringify(updatedSavedComparisons));

    return newSavedComparison;
  };

  // Удаление сохраненного сравнения
  const deleteSavedComparison = (id) => {
    const updatedSavedComparisons = savedComparisons.filter(comp => comp.id !== id);
    setSavedComparisons(updatedSavedComparisons);
    localStorage.setItem('savedRefinancingComparisons', JSON.stringify(updatedSavedComparisons));
  };

  // Загрузка сохраненного сравнения
  const loadSavedComparison = (id) => {
    const comparisonToLoad = savedComparisons.find(comp => comp.id === id);
    if (comparisonToLoad) {
      setFormData(comparisonToLoad.formData);
      setResults(comparisonToLoad.results);
      setComparisonData(comparisonToLoad.comparisonData);
      setSelectedBank(comparisonToLoad.bank);
      // При необходимости заново рассчитать график платежей
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Calculator className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                Калькулятор рефинансирования кредита
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              comparisonData={comparisonData}
              setComparisonData={setComparisonData}
              schedule={schedule}
              setSchedule={setSchedule}
              earlyRepaymentPeriods={earlyRepaymentPeriods}
              banks={banks}
              saveComparison={saveComparison}
              selectedBank={selectedBank}
              setSelectedBank={setSelectedBank}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} comparisonData={comparisonData} />
                
                {comparisonData && <ComparisonChart comparisonData={comparisonData} />}
                
                <PaymentSchedule 
                  schedule={schedule} 
                  results={results}
                  showFullSchedule={showFullSchedule}
                  setShowFullSchedule={setShowFullSchedule}
                />
              </>
            )}

            {/* --- Информационный контент о рефинансировании --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель предложений банков --- */}
            <BankOffersSidebar 
              banks={banks}
              currentAmount={parseFloat(formData.currentLoanAmount) || 0}
              additionalAmount={parseFloat(formData.additionalAmount) || 0}
              selectedBank={selectedBank}
              setSelectedBank={setSelectedBank}
              formData={formData}
              setFormData={setFormData}
              savedComparisons={savedComparisons}
              deleteSavedComparison={deleteSavedComparison}
              loadSavedComparison={loadSavedComparison}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Payment Schedule Table */
        .payment-schedule-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .payment-schedule-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default LoanRefinancingCalculatorPage;